import distance from '@turf/distance';
import { point as turfPoint } from '@turf/helpers';
import { create } from 'zustand';

export interface Measurement {
  points: [number, number][];
  draftPoint: [number, number] | null;
  totalDistance: number;
  draggedPointIndex: number | null;
  isClosed: boolean;
  area: number;
  isSnapToFirst: boolean;
}

interface UseMapMeasurementReturn {
  measurement: Measurement;
  addMeasurementPoint: (point: [number, number]) => void;
  setDraftPoint: (point: [number, number] | null) => void;
  setIsSnapToFirst: (snap: boolean) => void;
  startDraggingMeasurementPoint: (index: number) => void;
  updateMeasurementPoint: (index: number, point: [number, number]) => void;
  stopDraggingMeasurementPoint: () => void;
  closeMeasurementPolygon: () => void;
  clearMeasurement: () => void;
}

const initialMeasurement: Measurement = {
  points: [],
  draftPoint: null,
  totalDistance: 0,
  draggedPointIndex: null,
  isClosed: false,
  area: 0,
  isSnapToFirst: false,
};

function calculateTotalDistance(points: [number, number][]) {
  if (points.length < 2) return 0;

  return points.slice(1).reduce((total, point, index) => {
    const from = turfPoint(points[index]);
    const to = turfPoint(point);

    return total + distance(from, to, { units: 'kilometers' });
  }, 0);
}

function calculatePolygonArea(points: [number, number][]): number {
  if (points.length < 3) return 0;

  const n = points.length;
  const meanLat = points.reduce((sum, p) => sum + p[1], 0) / n;
  const cosLat = Math.cos((meanLat * Math.PI) / 180);
  const kmPerDegLat = 111.32;
  const kmPerDegLon = 111.32 * cosLat;

  let area = 0;
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    area +=
      points[i][0] * kmPerDegLon * (points[j][1] * kmPerDegLat) -
      points[j][0] * kmPerDegLon * (points[i][1] * kmPerDegLat);
  }
  return Math.abs(area) / 2;
}

const useMapMeasurementStore = create<UseMapMeasurementReturn>((set) => ({
  measurement: initialMeasurement,
  addMeasurementPoint: (point) => {
    set((state) => {
      const prev = state.measurement;

      if (prev.isClosed) return state;

      if (prev.points.length === 0) {
        return {
          measurement: {
            ...prev,
            points: [point],
            draftPoint: point,
            totalDistance: 0,
          },
        };
      }

      const from = turfPoint(prev.points[prev.points.length - 1]);
      const to = turfPoint(point);
      const segment = distance(from, to, { units: 'kilometers' });

      return {
        measurement: {
          ...prev,
          points: [...prev.points, point],
          draftPoint: point,
          totalDistance: prev.totalDistance + segment,
        },
      };
    });
  },
  setDraftPoint: (point) => {
    set((state) => {
      const prev = state.measurement;

      if (prev.isClosed) return state;

      const prevDraft = prev.draftPoint;
      const areNoPoints = prevDraft === null && point === null;
      const arePointsTheSame =
        prevDraft?.[0] === point?.[0] && prevDraft?.[1] === point?.[1];
      const same = areNoPoints || (!areNoPoints && arePointsTheSame);

      if (same) return state;

      return {
        measurement: {
          ...prev,
          draftPoint: point,
        },
      };
    });
  },
  setIsSnapToFirst: (snap) => {
    set((state) => {
      if (state.measurement.isSnapToFirst === snap) return state;
      return { measurement: { ...state.measurement, isSnapToFirst: snap } };
    });
  },
  closeMeasurementPolygon: () => {
    set((state) => {
      const prev = state.measurement;
      if (prev.points.length < 3) return state;

      const totalDistance = calculateTotalDistance([
        ...prev.points,
        prev.points[0],
      ]);
      const area = calculatePolygonArea(prev.points);

      return {
        measurement: {
          ...prev,
          isClosed: true,
          draftPoint: null,
          isSnapToFirst: false,
          totalDistance,
          area,
        },
      };
    });
  },
  startDraggingMeasurementPoint: (index) => {
    set((state) => {
      const prev = state.measurement;

      if (!prev.points[index] || prev.draggedPointIndex === index) {
        return state;
      }

      return {
        measurement: {
          ...prev,
          draftPoint: null,
          draggedPointIndex: index,
        },
      };
    });
  },
  updateMeasurementPoint: (index, point) => {
    set((state) => {
      const prev = state.measurement;

      if (!prev.points[index]) {
        return state;
      }

      const isSamePoint =
        prev.points[index][0] === point[0] &&
        prev.points[index][1] === point[1];

      if (isSamePoint) {
        return state;
      }

      const nextPoints = prev.points.map((existingPoint, currentIndex) =>
        currentIndex === index ? point : existingPoint
      ) as [number, number][];

      const totalDistance = prev.isClosed
        ? calculateTotalDistance([...nextPoints, nextPoints[0]])
        : calculateTotalDistance(nextPoints);
      const area = prev.isClosed ? calculatePolygonArea(nextPoints) : 0;

      return {
        measurement: {
          ...prev,
          points: nextPoints,
          draftPoint: null,
          totalDistance,
          area,
        },
      };
    });
  },
  stopDraggingMeasurementPoint: () => {
    set((state) => {
      const prev = state.measurement;

      if (prev.draggedPointIndex === null) {
        return state;
      }

      return {
        measurement: {
          ...prev,
          draggedPointIndex: null,
        },
      };
    });
  },
  clearMeasurement: () => {
    set({ measurement: initialMeasurement });
  },
}));

export function useMapMeasurement(): UseMapMeasurementReturn {
  return useMapMeasurementStore();
}
